import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { DataProviderElement, useDataState } from '../data/provider';
import { ContentProviderRegister } from '../content/register';
import { BasicTrackingFn, ComponentHeading, Content, CoreContents, DataMode, Params, SettingsProvider } from '../types';
import { ContentElementHasData, DataContentProps } from './types';
import { Loading } from '../design/loading/Loading';
import { ContextManager } from '../context/manager';
import { getParamsForContext, getPropositionFromContext } from '../context/utils';

type Props = {
  contentElementId: string;
  hideElement: boolean;
  setContentElementHasData: (contentElementId: string, hasData: ContentElementHasData) => void;
  content: Content;
  updateComponentSettings: (updates: Params) => void;
  paramsForData: Params;
  dataUrlRoot: string;
  dslOrContextLoading?: boolean;
  componentActions?: FunctionComponent<{ children: ReactNode }>;
  componentFooter?: FunctionComponent<{ children: ReactNode }>;
  componentSettings: Params;
  trackEvent?: BasicTrackingFn;
  componentMetadata?: SettingsProvider['metadata'];
  componentName?: string;
  componentHeadingTitle?: ComponentHeading['title'];
};

function ContentElement(props: Props) {
  const {
    content,
    updateComponentSettings,
    paramsForData,
    dataUrlRoot,
    componentActions,
    componentFooter,
    componentSettings,
    trackEvent,
    componentMetadata,
    componentName,
    componentHeadingTitle,
  } = props;
  const { type, dataOptions } = content;
  const context = ContextManager.getInstance().get();
  const paramsForContext = getParamsForContext(context);
  const proposition = getPropositionFromContext(context);
  if (!dataOptions) {
    const Element = ContentProviderRegister.getInstance().getContent(type);
    return (
      <Element
        updateSettings={updateComponentSettings}
        paramsForData={paramsForData}
        paramsForContext={paramsForContext}
        dataUrlRoot={dataUrlRoot}
        ComponentActionsContainer={componentActions}
        ComponentFooterContainer={componentFooter}
        componentSettings={componentSettings}
        trackEvent={trackEvent}
        componentMetadata={componentMetadata}
        componentName={componentName}
        componentHeadingTitle={componentHeadingTitle}
        proposition={proposition}
        {...content}
      />
    );
  }

  if (dataOptions.mode === DataMode.takeFromProvider) {
    return <DataReceivingContentElement {...props} />;
  }

  return (
    <DataProviderElement dataOptions={dataOptions} paramsForData={paramsForData} urlRoot={dataUrlRoot}>
      <DataReceivingContentElement {...props} />
    </DataProviderElement>
  );
}

function DataReceivingContentElement({
  content,
  contentElementId,
  updateComponentSettings,
  paramsForData,
  dataUrlRoot,
  componentActions,
  componentFooter,
  componentSettings,
  componentMetadata,
  trackEvent,
  setContentElementHasData,
  hideElement,
  componentName,
  componentHeadingTitle,
}: Props) {
  const { type } = content;
  const context = ContextManager.getInstance().get();
  const paramsForContext = getParamsForContext(context);
  const proposition = getPropositionFromContext(context);
  const { error, data, isDataLoadingFinished, isEnrichmentLoading, enrichmentError } = useDataState();
  const [overrideElementHasData, setOverrideElementHasData] = useState({});

  const Element = ContentProviderRegister.getInstance().getContent(
    !error ? type : CoreContents.dataError
  ) as FunctionComponent<DataContentProps>;

  useEffect(() => {
    if (!isDataLoadingFinished) {
      setContentElementHasData(contentElementId, 'isLoading');
      return;
    }
    if (overrideElementHasData[contentElementId] !== undefined) {
      setContentElementHasData(contentElementId, overrideElementHasData[contentElementId]);
      return;
    }
    setContentElementHasData(contentElementId, data ? data.hasDataInSomeGroupedSets() : false);
  }, [isDataLoadingFinished, overrideElementHasData]);

  if (hideElement) {
    return <></>;
  }

  const hasContentHaveData = (value: boolean | string) => {
    setOverrideElementHasData({ ...overrideElementHasData, [contentElementId]: value });
  };

  return (
    <>
      <Loader isLoading={!isDataLoadingFinished} />
      <Element
        {...content}
        updateSettings={updateComponentSettings}
        dataStamp={1}
        data={data}
        error={{
          message: error,
          title: 'Unable to load section',
          className: 'pt-4',
        }}
        isLoading={!isDataLoadingFinished}
        isEnrichmentLoading={isEnrichmentLoading}
        enrichmentError={enrichmentError}
        paramsForData={paramsForData}
        paramsForContext={paramsForContext}
        dataUrlRoot={dataUrlRoot}
        ComponentActionsContainer={componentActions}
        ComponentFooterContainer={componentFooter}
        componentSettings={componentSettings}
        componentMetadata={componentMetadata}
        trackEvent={trackEvent}
        hasContentHaveData={hasContentHaveData}
        componentName={componentName}
        componentHeadingTitle={componentHeadingTitle}
        proposition={proposition}
      />
    </>
  );
}

const Loader: React.FC<{ isLoading: boolean }> = ({ isLoading }) => {
  if (!isLoading) {
    return null;
  }
  return (
    <div className="timeseries relative pt-4 overflow-x-auto w-full max-w-full">
      <Loading isActive={isLoading} withIndicator />
    </div>
  );
};

export { ContentElement };
